import 'regenerator-runtime/runtime';
import { call, cancel, select, take, takeLatest } from 'redux-saga/effects';
import {
  AngebotsId,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  isAngebotsIdEqual,
  QuoteDbInitPayload,
  QuoteDbRequestPayload,
  QuoteDbSpartePayload,
} from '@ps20-frontend/shop-integration-redux';
import { App, getTarifbereich, Produktlinie } from '@ps20-frontend/frontend-models';
import {
  buildTaasRequestModelForShop,
  buildTaasRequestModelForStrecke,
  createHashedTaasData,
  getSelectedProduktlinie,
  getTaasVertragsoptionen,
} from '@ps20-frontend/preis-lib';
import {
  Dialogschritt,
  TaasRequestModel,
  TaasSalesProductName,
  TaasUriData,
} from '@ps20/bff-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { IAppState } from '../appState';
import { UnfActions } from '../unfActions';
import { selectSelectedProduktlinie } from '../selector/produkt.selector';
import {
  buildTaasUnfDataForShop,
  buildTaasUnfDataForStrecke,
} from './transformer/unfData.transformer';

export function* watchQuoteDB() {
  yield takeLatest(GlobalActions.QUOTEDB_DATA_REQUESTED, safeSaga(getQuoteDbData, App.UNF));
  yield takeLatest(UnfActions.QUOTEDB_REQUESTED, safeSaga(quoteDbInit, App.UNF));
}

export function* getQuoteDbData(action: GlobalAction<QuoteDbRequestPayload>) {
  const angebotsIds: AngebotsId[] = action.payload.angebotsIds;
  const globalPut = GlobalPut.getInstance();
  const state: IAppState = yield select();

  let taasRequestModel: TaasRequestModel<TaasUriData>;
  let selectedProduktlinie: Produktlinie;
  let alleProduktlinien: Array<Produktlinie>;
  const spartenAngebotsId: AngebotsId = { appId: App.UNF, angebotsId: state.angebotsId };

  if (angebotsIds && !angebotsIds.some(angebot => isAngebotsIdEqual(spartenAngebotsId, angebot))) {
    yield cancel();
    return;
  }
  if (
    (state.produkt && state.gesamtbeitrag !== 0) ||
    state.transientState.fehlerCodeTaas ||
    (state.vertragsoptionen.beibehalten && state.gesamtbeitragMitRabatt !== 0)
  ) {
    selectedProduktlinie = getSelectedProduktlinie(state.produkt);
    alleProduktlinien = state.produkt.produktlinien;
    if (action.payload.dialogschritt === Dialogschritt.SEITE_1_ANGEBOT_BERECHNEN) {
      taasRequestModel = yield call(buildTaasRequestModel, state);
    } else {
      taasRequestModel = yield call(buildTaasRequestModelShop, state);
    }
  }

  const quoteDbPayload: QuoteDbSpartePayload<TaasUriData> = {
    angebotsId: { appId: App.UNF, angebotsId: state.angebotsId },
    taasSparteRequestModel: taasRequestModel,
    selectedProduktlinie,
    alleProduktlinien,
    selbstbeteiligung: state.vertragsoptionen.selbstbeteiligung,
    gesamtpreis: state.gesamtbeitrag,
    gesamtpreisMitRabatt: state.gesamtbeitragMitRabatt,
    vertragslaufzeit: state.vertragsoptionen.vertragslaufzeit,
    antragsnummer: state.antragsnummer,
  };

  if (state.taasInfo.deltafaktoren) {
    quoteDbPayload.sparteErsatzInfo = {
      deltafaktoren: state.taasInfo.deltafaktoren.deltafaktoren,
      kuendigungLiegtVor: state.vertragsoptionen.vorvertragGekuendigt,
    };
  }
  yield call(globalPut.put, new GlobalActions().quoteDBDataSparteGenerated(quoteDbPayload));
}

export function* quoteDbInit() {
  const globalPut = GlobalPut.getInstance();
  yield take(GlobalActions.GESAMTPREIS_CHANGED);
  const state: IAppState = yield select();
  const fehlerCodeTaas = state.transientState.fehlerCodeTaas;
  if (fehlerCodeTaas === null) {
    yield cancel();
    return;
  }
  const quoteDbInitPayload: QuoteDbInitPayload = {
    ausloeserAngebotsId: { angebotsId: state.angebotsId, appId: App.UNF },
    dialogschritt: Dialogschritt.SEITE_1_ANGEBOT_BERECHNEN,
    fehlerCodeTaas,
  };
  yield call([globalPut, globalPut.put], new GlobalActions().quoteDBInit(quoteDbInitPayload));
}

export function buildTaasRequestModel(state: IAppState): TaasRequestModel<TaasUriData> {
  const taasData: TaasUriData = buildTaasUnfDataForStrecke(state);

  taasData.vpVorname = state.vp.vorname;
  taasData.vpNachname = state.vp.nachname;
  taasData.vpKundennummer = state.vp.kundennummer === '' ? null : state.vp.kundennummer;
  taasData.vnKundennummer = state.versicherungsnehmer.kundennummer;
  taasData.selectedProduktlinie =
    TaasSalesProductName[selectSelectedProduktlinie(state).produktName];
  taasData.profiSportRisk = state.vp?.profiSport?.profiSportRisk;
  taasData.profiId = state.vp?.profiSport?.sportId;
  taasData.profiSportName = state.vp?.profiSport?.sportName;

  return buildTaasRequestModelForStrecke(
    getTaasVertragsoptionen(state.vertragsoptionen),
    taasData,
    getTarifbereich(state.versicherungsnehmer),
  );
}

export function buildTaasRequestModelShop(state: IAppState): TaasRequestModel<TaasUriData> {
  const taasData: TaasUriData = buildTaasUnfDataForShop(state);
  taasData.vpVorname = state.vp.vorname;
  taasData.vpNachname = state.vp.nachname;
  taasData.vpKundennummer = state.vp.kundennummer === '' ? null : state.vp.kundennummer;
  taasData.vnKundennummer = state.versicherungsnehmer.kundennummer;
  taasData.selectedProduktlinie =
    TaasSalesProductName[selectSelectedProduktlinie(state).produktName];

  const newHashedTaasRequest = createHashedTaasData(taasData);
  return buildTaasRequestModelForShop(
    taasData,
    newHashedTaasRequest,
    getTaasVertragsoptionen(state.vertragsoptionen),
    state.taasInfo,
    getTarifbereich(state.versicherungsnehmer),
  );
}
